import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSyncExternalStore } from 'react';
import { useAuthStore } from './authStore.js';
import { syncUiStore } from './syncService.js';
import { api, ApiError } from '../../lib/apiClient.js';
import { formatClock } from '../../lib/datetime.js';
import { msg } from '../../lib/messages.js';
import './account.css';

function useSyncUi() {
  return useSyncExternalStore(
    syncUiStore.subscribe,
    () => syncUiStore.getState(),
    () => syncUiStore.getState(),
  );
}

export function AccountSection(): JSX.Element {
  const status = useAuthStore((s) => s.status);
  const me = useAuthStore((s) => s.me);
  const logout = useAuthStore((s) => s.logout);
  const sync = useSyncUi();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [password, setPassword] = useState('');
  const [delError, setDelError] = useState<string | null>(null);
  const [buying, setBuying] = useState(false);

  const buy = async (): Promise<void> => {
    setBuying(true);
    try {
      const { redirectUrl } = await api.checkout('vipps');
      // Full-page handoff to the payment provider (dev: self-completes).
      window.location.href = redirectUrl;
    } catch {
      setBuying(false);
    }
  };

  const onDelete = async (): Promise<void> => {
    setDelError(null);
    try {
      await api.deleteAccount(password);
      await logout();
      setConfirmDelete(false);
    } catch (err) {
      setDelError(err instanceof ApiError ? err.message : 'Feil');
    }
  };

  return (
    <section className="settings__section account">
      <h2 className="settings__section-title">{msg.account.section}</h2>

      {status !== 'authed' || !me ? (
        <Link to="/logg-inn" className="account__login-row">
          {msg.account.loggedOut}
        </Link>
      ) : (
        <>
          <div className="account__row">
            <span className="account__label">{msg.account.email}</span>
            <span className="account__value">{me.email}</span>
          </div>

          <div className="account__row">
            <span className="account__label">
              {me.entitlement === 'paid' ? msg.account.purchaseYes : msg.account.purchaseNo}
            </span>
            {me.entitlement !== 'paid' && (
              <button type="button" className="account__link" disabled={buying} onClick={() => void buy()}>
                {buying ? msg.auth.working : msg.account.buy}
              </button>
            )}
          </div>

          <div className="account__row">
            <span className="account__label">{msg.account.lastSynced}</span>
            <span className="account__value">
              {sync.syncing
                ? msg.account.syncing
                : sync.error
                  ? msg.account.syncError
                  : sync.lastSyncedAt
                    ? `${formatClock(sync.lastSyncedAt)} ✓`
                    : '—'}
            </span>
          </div>

          <div className="account__actions">
            <button type="button" className="btn btn--ghost" onClick={() => void logout()}>
              {msg.account.logout}
            </button>
            <button
              type="button"
              className="btn btn--ghost account__danger"
              onClick={() => setConfirmDelete(true)}
            >
              {msg.account.deleteAccount}
            </button>
          </div>
        </>
      )}

      {confirmDelete && (
        <div className="sheet-backdrop" onClick={() => setConfirmDelete(false)}>
          <div className="sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="sheet__handle" />
            <h2 className="sheet__title">{msg.account.deleteConfirm}</h2>
            <p className="sheet__body">{msg.account.deleteBody}</p>
            <input
              className="account__password"
              type="password"
              placeholder={msg.auth.password}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {delError && <div className="auth__error">{delError}</div>}
            <div className="sheet__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setConfirmDelete(false)}>
                {msg.tracks.cancel}
              </button>
              <button type="button" className="btn btn--accent" onClick={() => void onDelete()}>
                {msg.account.deleteAccount}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
